<template>
  <Transition name="ui-sheet">
    <div v-if="open" class="ui-sheet" :style="{ zIndex: String(zIndex) }">
      <div class="ui-sheet__scrim" @click="handleScrimClick" />
      <section
        ref="sheetRef"
        class="ui-sheet__panel"
        :style="{ height: `${heightPx}px` }"
        role="dialog"
        :aria-modal="ariaModal"
        @keydown.capture="handleKeydown"
        @click.stop
      >
        <button
          class="ui-sheet__handle"
          type="button"
          aria-label="Drag sheet"
          @pointerdown="startDrag"
        >
          <span class="ui-sheet__handle-bar" aria-hidden="true" />
        </button>
        <div class="ui-sheet__body scroll">
          <slot />
        </div>
      </section>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    open: boolean;
    snapPoints?: number[]; // 0..1 (fraction of viewport height)
    initialSnap?: number;
    zIndex?: number;
    closeOnScrim?: boolean;
    ariaModal?: boolean;
  }>(),
  {
    snapPoints: () => [0.28, 0.62, 0.92],
    initialSnap: 1,
    zIndex: 3900,
    closeOnScrim: true,
    ariaModal: true
  }
);

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
  (e: "snap", value: number): void;
}>();

const viewportH = ref<number>(typeof window === "undefined" ? 800 : window.innerHeight);
const currentSnap = ref<number>(clampSnap(props.initialSnap, props.snapPoints.length));
const heightPx = ref<number>(Math.round(props.snapPoints[currentSnap.value] * viewportH.value));
const sheetRef = ref<HTMLElement | null>(null);
let restoreFocusEl: HTMLElement | null = null;

function cleanupDrag() {
  dragging = false;
  window.removeEventListener("pointermove", onDragMove);
  window.removeEventListener("pointerup", onDragEnd);
  window.removeEventListener("pointercancel", onDragEnd);
}

function restoreFocus() {
  const el = restoreFocusEl;
  restoreFocusEl = null;
  if (el) window.setTimeout(() => el.focus?.(), 0);
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      restoreFocusEl = (document.activeElement as HTMLElement | null) ?? null;
      currentSnap.value = clampSnap(props.initialSnap, props.snapPoints.length);
      heightPx.value = snapHeightPx();
      window.setTimeout(() => focusFirst(), 0);
      return;
    }

    cleanupDrag();
    restoreFocus();
  }
);

watch(
  () => [props.snapPoints, props.initialSnap] as const,
  () => {
    currentSnap.value = clampSnap(props.initialSnap, props.snapPoints.length);
    heightPx.value = snapHeightPx();
  },
  { deep: true }
);

function clampSnap(index: number, length: number) {
  if (!Number.isFinite(index)) return 0;
  return Math.max(0, Math.min(length - 1, Math.floor(index)));
}

function snapHeightPx(index = currentSnap.value) {
  const snap = props.snapPoints[Math.max(0, Math.min(props.snapPoints.length - 1, index))] ?? 0.62;
  return Math.max(180, Math.round(snap * viewportH.value));
}

function handleScrimClick() {
  if (!props.closeOnScrim) return;
  emit("update:open", false);
}

function focusableElements(root: HTMLElement): HTMLElement[] {
  const nodes = Array.from(
    root.querySelectorAll<HTMLElement>(
      [
        "[autofocus]",
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "[tabindex]:not([tabindex='-1'])"
      ].join(",")
    )
  );
  return nodes.filter((el) => {
    const style = window.getComputedStyle(el);
    return style.visibility !== "hidden" && style.display !== "none";
  });
}

function focusFirst() {
  const panel = sheetRef.value;
  if (!panel) return;
  const focusables = focusableElements(panel);
  focusables[0]?.focus();
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== "Tab") return;
  const panel = sheetRef.value;
  if (!panel) return;
  const focusables = focusableElements(panel);
  if (focusables.length === 0) {
    event.preventDefault();
    return;
  }
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (event.shiftKey) {
    if (!active || active === first || !panel.contains(active)) {
      event.preventDefault();
      last.focus();
    }
    return;
  }

  if (!active || active === last || !panel.contains(active)) {
    event.preventDefault();
    first.focus();
  }
}

let dragging = false;
let startY = 0;
let startH = 0;

function startDrag(event: PointerEvent) {
  if (!props.open) return;
  dragging = true;
  startY = event.clientY;
  startH = heightPx.value;
  if (event.cancelable) event.preventDefault();
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  window.addEventListener("pointermove", onDragMove, { passive: false });
  window.addEventListener("pointerup", onDragEnd, { once: true });
  window.addEventListener("pointercancel", onDragEnd, { once: true });
}

function onDragMove(event: PointerEvent) {
  if (!dragging) return;
  if (event.cancelable) event.preventDefault();
  const delta = startY - event.clientY;
  const next = Math.max(140, Math.min(viewportH.value, startH + delta));
  heightPx.value = Math.round(next);
}

function onDragEnd() {
  if (!dragging) return;
  dragging = false;
  window.removeEventListener("pointermove", onDragMove);

  const target = heightPx.value / viewportH.value;
  let bestIndex = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < props.snapPoints.length; i++) {
    const dist = Math.abs(props.snapPoints[i] - target);
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = i;
    }
  }

  currentSnap.value = bestIndex;
  heightPx.value = snapHeightPx(bestIndex);
  emit("snap", bestIndex);
}

function onResize() {
  viewportH.value = window.innerHeight;
  heightPx.value = snapHeightPx(currentSnap.value);
}

onMounted(() => {
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
  cleanupDrag();
  restoreFocus();
});
</script>

<style scoped>
.ui-sheet {
  position: fixed;
  inset: 0;
  display: grid;
  align-items: end;
}

.ui-sheet-enter-active,
.ui-sheet-leave-active {
  transition: opacity var(--dur-2) var(--ease-out);
}

.ui-sheet-enter-from,
.ui-sheet-leave-to {
  opacity: 0;
}

.ui-sheet-enter-active .ui-sheet__scrim,
.ui-sheet-leave-active .ui-sheet__scrim {
  transition: opacity var(--dur-2) var(--ease-out);
}

.ui-sheet-enter-from .ui-sheet__scrim,
.ui-sheet-leave-to .ui-sheet__scrim {
  opacity: 0;
}

.ui-sheet-enter-active .ui-sheet__panel,
.ui-sheet-leave-active .ui-sheet__panel {
  transition: transform var(--dur-3) var(--ease-out);
  will-change: transform;
}

.ui-sheet-enter-from .ui-sheet__panel,
.ui-sheet-leave-to .ui-sheet__panel {
  transform: translateY(14px);
}

.ui-sheet__scrim {
  position: absolute;
  inset: 0;
  background: var(--sheet-scrim);
  backdrop-filter: blur(4px);
}

.ui-sheet__panel {
  position: relative;
  width: 100%;
  background: color-mix(in srgb, var(--bg-2) 92%, transparent);
  border-top: 1px solid var(--border-1);
  border-radius: 22px 22px 0 0;
  box-shadow: var(--shadow-2);
  overflow: hidden;
  padding-bottom: var(--mobile-nav-offset, env(safe-area-inset-bottom));
}

.ui-sheet__handle {
  width: 100%;
  display: grid;
  place-items: center;
  padding: 0.65rem 0 0.3rem;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.ui-sheet__handle:active {
  cursor: grabbing;
}

.ui-sheet__handle-bar {
  width: 54px;
  height: 5px;
  border-radius: 999px;
  background: var(--sheet-handle);
}

.ui-sheet__body {
  height: calc(100% - 34px);
  padding: 0 1rem 1.2rem;
}
</style>
